import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';
import { buildActionKey, runLockedAction } from '../utils/actionLock';

class Api {
  private instance: AxiosInstance;

  constructor() {
    // Prefer env; fall back to same origin so a local .env can't break prod CSP (https-only connect-src).
    const envUrl = (process.env.REACT_APP_API_URL || '').trim();
    const sameOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://127.0.0.1:8000';
    let baseURL = envUrl || sameOrigin;

    // Host-only values (e.g. "127.0.0.1:8000") need a protocol; match the page scheme on https.
    if (baseURL && !baseURL.match(/^https?:\/\//)) {
      const isHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
      baseURL = `${isHttps ? 'https' : 'http'}://${baseURL}`;
    }

    baseURL = baseURL.replace(/\/+$/, '');
    // All dashboard requests use paths like `/api/admin/...`. If baseURL ends with `/api`,
    // axios combines to `/api/api/admin/...` and Laravel returns 404.
    baseURL = baseURL.replace(/\/api$/i, '').replace(/\/+$/, '');

    this.instance = axios.create({
      baseURL: baseURL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Handle per-page parameter and external URLs
        try {
          if (config.url && config.url.startsWith('http')) {
            // External URL - handle per-page if present
            const url = new URL(config.url);
            const perPage = url.searchParams.get('per-page');
            if (perPage) {
              config.headers['per-page'] = perPage.toString();
              url.searchParams.delete('per-page');
              config.url = url.toString();
            }
            return axios(config);
          }
        } catch (error) {
          // If URL parsing fails, continue without modification
        }

        // For FormData requests, let Axios set the Content-Type automatically
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data;
      },
      (error) => {
        // Don't show error messages if we're on the login page
        const isLoginPage = window.location.pathname === '/login';

        if (error.response) {
          const { status, data } = error.response;

          switch (status) {
            case 401:
              if (!isLoginPage) {
                message.error('Unauthorized access');
              }
              localStorage.removeItem('token');
              if (!isLoginPage) {
                window.location.href = '/login';
              }
              break;
            case 403:
              if (!isLoginPage) {
                message.error('Access forbidden');
              }
              break;
            case 404:
              if (!isLoginPage) {
                message.error('Resource not found');
              }
              break;
            case 500:
              if (!isLoginPage) {
                message.error('Internal server error');
              }
              break;
            default:
              if (!isLoginPage) {
                message.error(data?.message || 'An error occurred');
              }
          }
        } else if (error.request) {
          if (error.code !== 'ERR_CANCELED' && !isLoginPage) {
            message.error('Network error. Please check your connection.');
          }
        } else {
          if (!isLoginPage) {
            // message.error('An unexpected error occurred');
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // GET request — success responses are unwrapped to `response.data` by the interceptor.
  // Default `any`: Laravel bodies vary; use Api.get<MyShape>(...) where you want typing.
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    if (config?.baseURL) {
      const configWithBaseURL = {
        ...config,
        baseURL: config.baseURL,
      };
      return this.instance.get(url, configWithBaseURL) as Promise<T>;
    }
    return this.instance.get(url, config) as Promise<T>;
  }

  private runMutation<T>(
    method: 'post' | 'put' | 'patch' | 'delete',
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const key = buildActionKey(method, url, data);
    return runLockedAction(key, () => this.instance[method](url, data, config) as Promise<T>);
  }

  async post<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.runMutation<T>('post', url, data, config);
  }

  async put<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.runMutation<T>('put', url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const key = buildActionKey('delete', url, config?.data);
    return runLockedAction(key, () => this.instance.delete(url, config) as Promise<T>);
  }

  async patch<T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.runMutation<T>('patch', url, data, config);
  }
}

export default new Api();
